import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useSendMessage } from 'crustate/react';
import { useTranslate } from '@awardit/react-use-translate';
import { FilterableProductList, Wrapper, PaginatedProductList, ProductList, FilterableProductListHint } from '@crossroads/ui-components';
import { allProducts } from '@crossroads/shop-state/all-products';
import { load } from '@crossroads/shop-state/route';
import { load as load$2 } from '@crossroads/shop-state/brand';
import { load as load$1 } from '@crossroads/shop-state/popular';
import { ProductListHero, ProductListHeroHint } from '../../../components/ProductListHero/index.js';
import { CategoryNavigation } from '../../../components/CategoryNavigation/index.js';
import { FilterIconSvg, SortIconSvg } from '../../../icons/index.js';
import { useBreadcrumbLinks } from '../../../helpers/use-breadcrumb-links.js';
import { useLocation } from 'react-router';
import { getCategoryMeta } from '../../../helpers/get-meta.js';
import { Button } from '../../../components/Button/index.js';
import { AdventCalendarHero } from '../../../components/AdventCalendarHero/index.js';
import cn from 'classnames';
import styles from 'shop-views/ProductListView.scss';

var _excluded = ["variant", "category", "home", "productList", "isUpdating", "pageSize", "StoreInfoContext", "ProductCardComponent", "hasPagination", "productsPerRow", "exponentiation", "hasNonLinearSlider", "productsLink", "title", "hasFilters", "hasCategorySubNavigation", "filterIcon", "sortIcon", "uiContext", "pointsPriceId", "hasPoints", "images", "heroDescription", "sortButtonSide", "hasTitleParts", "currentBreadcrumb", "onAdventCalendarPath", "hasAdventCalendarHero", "hasAlternativeHeroLayout", "hasTotalCount", "hasAdventCalendarBackButton", "hasCategoryParentBanner", "priceLock"];

var getSubCategories = category => {
  var _category$parent;

  var subCategories = [];

  if (!category) {
    return subCategories;
  }

  var siblings = (_category$parent = category.parent) == null ? void 0 : _category$parent.children;

  if (siblings || category.children) {
    var mergedCategories = siblings && siblings.length > 0 ? siblings : category.children;

    if (mergedCategories.length > 0) {
      subCategories.push(...mergedCategories);
    }
  }

  return subCategories;
};

var getHeroImages = category => {
  var images = {};

  if (category.clsCatfieldsGeneralImgS) {
    images.small = {
      x1: category.clsCatfieldsGeneralImgS.x1,
      x2: category.clsCatfieldsGeneralImgS.x2
    };
  }

  if (category.clsCatfieldsGeneralImgM) {
    images.medium = {
      x1: category.clsCatfieldsGeneralImgM.x1,
      x2: category.clsCatfieldsGeneralImgM.x2
    };
  }

  if (category.clsCatfieldsGeneralImgL) {
    images.large = {
      x1: category.clsCatfieldsGeneralImgL.x1,
      x2: category.clsCatfieldsGeneralImgL.x2
    };
  }

  return images;
};

var getLoadFn = (variant, title, sendMessage) => {
  switch (variant) {
    case "allproducts":
      return location => sendMessage(allProducts(location));

    case "category":
      return location => sendMessage(load(location, true));

    case "brands":
      return location => sendMessage(load$2(title, location, true));

    case "popular":
      return location => sendMessage(load$1(location, true));

    default:
      return location => sendMessage(load(location, true));
  }
};

var getCategoryParentBannerImages = (home, images, pathname) => {
  var _home$adventCalendar;

  var parentCategoryBanner = ((_home$adventCalendar = home.adventCalendar) == null ? void 0 : _home$adventCalendar.category) !== undefined && home.adventCalendar.category !== null ? home.adventCalendar.category : null; // Only show parentCategoryBanner for advent-calendar-categories

  if (Object.keys(images).length === 0 && parentCategoryBanner !== null && pathname.includes("julkalender")) {
    images = getHeroImages(parentCategoryBanner);
  }

  return images;
};

var ProductListView = _ref => {
  var variant = _ref.variant,
      category = _ref.category,
      home = _ref.home,
      productList = _ref.productList,
      isUpdating = _ref.isUpdating,
      pageSize = _ref.pageSize,
      StoreInfoContext = _ref.StoreInfoContext,
      ProductCardComponent = _ref.ProductCardComponent,
      hasPagination = _ref.hasPagination,
      productsPerRow = _ref.productsPerRow,
      exponentiation = _ref.exponentiation,
      hasNonLinearSlider = _ref.hasNonLinearSlider,
      productsLink = _ref.productsLink,
      title = _ref.title,
      hasFilters = _ref.hasFilters,
      hasCategorySubNavigation = _ref.hasCategorySubNavigation,
      filterIcon = _ref.filterIcon,
      sortIcon = _ref.sortIcon,
      uiContext = _ref.uiContext,
      pointsPriceId = _ref.pointsPriceId,
      hasPoints = _ref.hasPoints,
      images = _ref.images,
      heroDescription = _ref.heroDescription,
      sortButtonSide = _ref.sortButtonSide,
      hasTitleParts = _ref.hasTitleParts,
      currentBreadcrumb = _ref.currentBreadcrumb,
      onAdventCalendarPath = _ref.onAdventCalendarPath,
      _ref$hasAdventCalenda = _ref.hasAdventCalendarHero,
      hasAdventCalendarHero = _ref$hasAdventCalenda === void 0 ? false : _ref$hasAdventCalenda,
      _ref$hasAlternativeHe = _ref.hasAlternativeHeroLayout,
      hasAlternativeHeroLayout = _ref$hasAlternativeHe === void 0 ? false : _ref$hasAlternativeHe,
      _ref$hasTotalCount = _ref.hasTotalCount,
      hasTotalCount = _ref$hasTotalCount === void 0 ? false : _ref$hasTotalCount,
      _ref$hasAdventCalenda2 = _ref.hasAdventCalendarBackButton,
      hasAdventCalendarBackButton = _ref$hasAdventCalenda2 === void 0 ? false : _ref$hasAdventCalenda2,
      _ref$hasCategoryParen = _ref.hasCategoryParentBanner,
      hasCategoryParentBanner = _ref$hasCategoryParen === void 0 ? false : _ref$hasCategoryParen,
      priceLock = _ref.priceLock,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);

  var t = useTranslate();
  var sendMessage = useSendMessage();
  var numPages = Math.ceil(productList.totalCount / pageSize);
  var breadcrumbLinks = useBreadcrumbLinks(StoreInfoContext, {
    current: currentBreadcrumb
  });

  var _useLocation = useLocation(),
      pathname = _useLocation.pathname,
      search = _useLocation.search;

  var meta = {
    title: "",
    data: []
  };
  var subCategories = getSubCategories(category);
  var onAdventCalendar = pathname.includes(onAdventCalendarPath);
  var loadFn = getLoadFn(variant, title, sendMessage);

  if (category) {
    meta = getCategoryMeta(category);
    images = getHeroImages(category);

    if (home && hasCategoryParentBanner) {
      images = getCategoryParentBannerImages(home, images, pathname);
    }
  }

  return /*#__PURE__*/React.createElement("div", {
    className: cn(styles.productListView, {
      [styles.adventCalendarCategory]: category && onAdventCalendar
    })
  }, /*#__PURE__*/React.createElement(Helmet, {
    title: variant === "category" ? meta.title : title,
    meta: meta.data
  }), onAdventCalendar && hasAdventCalendarHero && category ? /*#__PURE__*/React.createElement(AdventCalendarHero, {
    hasAlternativeHeroLayout: hasAlternativeHeroLayout,
    hasTotalCount: hasTotalCount,
    category: category,
    home: home
  }) : /*#__PURE__*/React.createElement(ProductListHero, {
    title: title,
    totalCount: productList.totalCount,
    breadcrumbLinks: breadcrumbLinks,
    breadcrumbCurrent: title,
    images: images,
    description: heroDescription,
    hasTitleParts: hasTitleParts
  }), /*#__PURE__*/React.createElement(CategoryNavigation, {
    isActive: hasCategorySubNavigation,
    subCategories: subCategories
  }), hasFilters ? /*#__PURE__*/React.createElement(FilterableProductList, _extends({}, rest, {
    updating: isUpdating,
    productList: productList,
    load: loadFn,
    category: title,
    uiContext: uiContext,
    filterIcon: filterIcon != null ? filterIcon : /*#__PURE__*/React.createElement("img", {
      src: FilterIconSvg,
      className: styles.filterIcon
    }),
    sortIcon: sortIcon != null ? sortIcon : /*#__PURE__*/React.createElement("img", {
      src: SortIconSvg,
      className: styles.sortIcon
    }),
    numPages: numPages,
    breadcrumbLinks: [{
      pathname: productsLink,
      title,
      search,
      hint: {
        name: title
      }
    }],
    ProductCardComponent: ProductCardComponent,
    usePagination: hasPagination,
    productsPerRow: productsPerRow,
    exponentiation: exponentiation,
    nonLinearSlider: hasNonLinearSlider,
    pointsPriceId: pointsPriceId,
    usePoints: hasPoints,
    sortButtonSide: sortButtonSide,
    priceLock: priceLock
  })) : /*#__PURE__*/React.createElement(Wrapper, {
    className: styles.productList
  }, hasPagination ? /*#__PURE__*/React.createElement(PaginatedProductList, _extends({}, rest, {
    productList: productList,
    numPages: numPages,
    uiContext: uiContext,
    updating: isUpdating,
    category: title,
    ProductCardComponent: ProductCardComponent,
    breadcrumbLinks: [{
      pathname: productsLink,
      title,
      search,
      hint: {
        name: title
      }
    }],
    productsPerRow: productsPerRow,
    pointsPriceId: pointsPriceId
  })) : /*#__PURE__*/React.createElement(ProductList, _extends({}, rest, {
    products: productList.items,
    heading: title,
    category: title,
    breadcrumbLinks: [{
      pathname: productsLink,
      title,
      search,
      hint: {
        name: title
      }
    }],
    ProductCardComponent: ProductCardComponent,
    productsPerRow: productsPerRow,
    loading: isUpdating,
    pointsPriceId: pointsPriceId
  }))), onAdventCalendar && hasAdventCalendarBackButton && /*#__PURE__*/React.createElement("div", {
    className: styles.adventCalendarBack
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "primary",
    to: "/" + t("ADVENT_CALENDAR.CATEGORY_URL")
  }, t("ADVENT_CALENDAR.BACK"))));
};
var HintProductListView = _ref2 => {
  var title = _ref2.title,
      StoreInfoContext = _ref2.StoreInfoContext,
      loadingText = _ref2.loadingText;
  var breadcrumbLinks = useBreadcrumbLinks(StoreInfoContext, {
    current: title
  });
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Helmet, {
    title: title
  }), /*#__PURE__*/React.createElement(ProductListHeroHint, {
    title: title,
    breadcrumbLinks: breadcrumbLinks != null ? breadcrumbLinks : [],
    breadcrumbCurrent: title != null ? title : "",
    loadingText: loadingText
  }), /*#__PURE__*/React.createElement(FilterableProductListHint, null));
};

export { HintProductListView, ProductListView };
//# sourceMappingURL=index.js.map
